/**
 * Client side of the AI reliability system. The server decides capability,
 * entitlement and allowance (server/config/ai-capabilities.ts); this module
 * only asks the student for acknowledgements and reports outcomes.
 */
import { toast } from "sonner";
import { AI_COPY, CAPABILITY_LABELS, type AICapability, type CapabilityOutcome } from "@shared/ai-capabilities";

export interface AINotice {
  type: "ai_notice";
  requiresAcknowledgement: boolean;
  key: string;
  recommended: AICapability;
  message: string;
}

export function isAINotice(value: unknown): value is AINotice {
  return !!value && typeof value === "object" && (value as { type?: unknown }).type === "ai_notice";
}

type Resolver = (accepted: boolean) => void;
type Listener = (notice: AINotice, resolve: Resolver) => void;
let listener: Listener | null = null;

/** Mounted once by <AIAcknowledgementHost/>. */
export function registerAcknowledgementListener(fn: Listener | null) {
  listener = fn;
}

/**
 * Shows the one-time "Standard AI on complex maths" notice. Resolves true
 * when the student chooses to continue. Without a mounted host (tests,
 * embedded views) it declines rather than sending silently.
 */
export function requestAcknowledgement(notice: AINotice): Promise<boolean> {
  if (!listener) return Promise.resolve(false);
  return new Promise((resolve) => listener!(notice, resolve));
}

/** Tells the student when they didn't get the capability they'd expect. Quiet otherwise. */
export function reportCapabilityOutcome(outcome: CapabilityOutcome | null | undefined) {
  if (!outcome || outcome.used !== "standard") return;
  if (outcome.reason === "unavailable") {
    toast.message(`${CAPABILITY_LABELS[outcome.recommended]} is temporarily unavailable`, {
      description: AI_COPY.advancedUnavailable,
    });
  } else if (outcome.reason === "limit_reached") {
    toast.message("Answered with Standard AI", { description: AI_COPY.advancedLimitReached });
  }
}
